import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import * as xcloud from '@/lib/xcloudService';
import type { WorkspaceIntegration } from '@/types/xcloud';
import { useWorkspace } from '@/context/WorkspaceContext';

// ── Connection / permission hooks ─────────────────────────────────────────────

export function useXcloudConnection(workspaceId: string | undefined) {
  return useQuery({
    queryKey: ['xcloud-connection', workspaceId],
    queryFn: async () => {
      if (!workspaceId) return null;
      const { data, error } = await supabase
        .from('workspace_integrations')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('provider', 'xcloud')
        .maybeSingle();
      if (error) throw error;
      return data as WorkspaceIntegration | null;
    },
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

export function useHostingPermission() {
  const { userRole } = useWorkspace();
  const canView = userRole === 'owner' || userRole === 'admin' || userRole === 'manager';
  const canManage = userRole === 'owner' || userRole === 'admin';
  return { canView, canManage, role: userRole };
}

// ── Connect / Disconnect mutations ────────────────────────────────────────────

export function useConnectXCloud(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ token }: { token: string }) => xcloud.connectXCloud(workspaceId, token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['xcloud-connection', workspaceId] });
    },
  });
}

export function useDisconnectXCloud(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => xcloud.disconnectXCloud(workspaceId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['xcloud-connection', workspaceId] });
      qc.invalidateQueries({ queryKey: ['xcloud', workspaceId] });
    },
  });
}

// ── Server hooks ──────────────────────────────────────────────────────────────

export function useXcloudServers(workspaceId: string | undefined, page: number = 1, force?: boolean) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'servers', page],
    queryFn: () => xcloud.listServers(workspaceId!, page, force),
    enabled: !!workspaceId,
    staleTime: 300_000,
  });
}

export function useXcloudServer(workspaceId: string | undefined, serverUuid: string | undefined, force?: boolean) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'server', serverUuid],
    queryFn: () => xcloud.getServer(workspaceId!, serverUuid!, force),
    enabled: !!workspaceId && !!serverUuid,
    staleTime: 300_000,
  });
}

export function useServerDatabases(workspaceId: string | undefined, serverUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'server-databases', serverUuid],
    queryFn: () => xcloud.listServerDatabases(workspaceId!, serverUuid!),
    enabled: !!workspaceId && !!serverUuid,
    staleTime: 300_000,
  });
}

export function useServerCronJobs(workspaceId: string | undefined, serverUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'server-cron-jobs', serverUuid],
    queryFn: () => xcloud.listServerCronJobs(workspaceId!, serverUuid!),
    enabled: !!workspaceId && !!serverUuid,
    staleTime: 300_000,
  });
}

export function useServerMonitoring(workspaceId: string | undefined, serverUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'server-monitoring', serverUuid],
    queryFn: () => xcloud.getServerMonitoring(workspaceId!, serverUuid!),
    enabled: !!workspaceId && !!serverUuid,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useServerPhpVersions(workspaceId: string | undefined, serverUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'server-php-versions', serverUuid],
    queryFn: () => xcloud.getServerPhpVersions(workspaceId!, serverUuid!),
    enabled: !!workspaceId && !!serverUuid,
    staleTime: 600_000,
  });
}

export function useServerSudoUsers(workspaceId: string | undefined, serverUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'server-sudo-users', serverUuid],
    queryFn: () => xcloud.listServerSudoUsers(workspaceId!, serverUuid!),
    enabled: !!workspaceId && !!serverUuid,
    staleTime: 300_000,
  });
}

export function useServerTasks(workspaceId: string | undefined, serverUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'server-tasks', serverUuid],
    queryFn: () => xcloud.listServerTasks(workspaceId!, serverUuid!),
    enabled: !!workspaceId && !!serverUuid,
    staleTime: 60_000,
  });
}

export function useServerSites(workspaceId: string | undefined, serverUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'server-sites', serverUuid],
    queryFn: () => xcloud.listServerSites(workspaceId!, serverUuid!),
    enabled: !!workspaceId && !!serverUuid,
    staleTime: 300_000,
  });
}

// ── Site hooks ────────────────────────────────────────────────────────────────

export function useXcloudSites(workspaceId: string | undefined, page: number = 1, force?: boolean) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'sites', page],
    queryFn: () => xcloud.listSites(workspaceId!, page, force),
    enabled: !!workspaceId,
    staleTime: 300_000,
  });
}

export function useXcloudSite(workspaceId: string | undefined, siteUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'site', siteUuid],
    queryFn: () => xcloud.getSite(workspaceId!, siteUuid!),
    enabled: !!workspaceId && !!siteUuid,
    staleTime: 300_000,
  });
}

export function useSiteSsl(workspaceId: string | undefined, siteUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'site-ssl', siteUuid],
    queryFn: () => xcloud.getSiteSsl(workspaceId!, siteUuid!),
    enabled: !!workspaceId && !!siteUuid,
    staleTime: 600_000,
  });
}

export function useSiteBackups(workspaceId: string | undefined, siteUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'site-backups', siteUuid],
    queryFn: () => xcloud.listSiteBackups(workspaceId!, siteUuid!),
    enabled: !!workspaceId && !!siteUuid,
    staleTime: 120_000,
  });
}

export function useSiteMonitoring(workspaceId: string | undefined, siteUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'site-monitoring', siteUuid],
    queryFn: () => xcloud.getSiteMonitoring(workspaceId!, siteUuid!),
    enabled: !!workspaceId && !!siteUuid,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });
}

export function useSiteEvents(workspaceId: string | undefined, siteUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'site-events', siteUuid],
    queryFn: () => xcloud.listSiteEvents(workspaceId!, siteUuid!),
    enabled: !!workspaceId && !!siteUuid,
    staleTime: 120_000,
  });
}

export function useDeploymentLogs(workspaceId: string | undefined, siteUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'deployment-logs', siteUuid],
    queryFn: () => xcloud.listDeploymentLogs(workspaceId!, siteUuid!),
    enabled: !!workspaceId && !!siteUuid,
    staleTime: 120_000,
  });
}

export function useSiteGit(workspaceId: string | undefined, siteUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'site-git', siteUuid],
    queryFn: () => xcloud.getSiteGit(workspaceId!, siteUuid!),
    enabled: !!workspaceId && !!siteUuid,
    staleTime: 600_000,
  });
}

export function useSiteSsh(workspaceId: string | undefined, siteUuid: string | undefined) {
  return useQuery({
    queryKey: ['xcloud', workspaceId, 'site-ssh', siteUuid],
    queryFn: () => xcloud.getSiteSsh(workspaceId!, siteUuid!),
    enabled: !!workspaceId && !!siteUuid,
    staleTime: 600_000,
  });
}

// ── Write mutations ───────────────────────────────────────────────────────────

export function useRebootServer(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (serverUuid: string) => xcloud.rebootServer(workspaceId, serverUuid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['xcloud', workspaceId, 'servers'] });
    },
  });
}

export function useTriggerBackup(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (siteUuid: string) => xcloud.triggerSiteBackup(workspaceId, siteUuid),
    onSuccess: (_data, siteUuid) => {
      qc.invalidateQueries({ queryKey: ['xcloud', workspaceId, 'site-backups', siteUuid] });
    },
  });
}

export function usePurgeCache(workspaceId: string) {
  return useMutation({
    mutationFn: (siteUuid: string) => xcloud.purgeSiteCache(workspaceId, siteUuid),
  });
}

export function useDeleteSudoUser(workspaceId: string, serverUuid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (sudoUserUuid: string) => xcloud.deleteSudoUser(workspaceId, serverUuid, sudoUserUuid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['xcloud', workspaceId, 'server-sudo-users', serverUuid] });
    },
  });
}
